/**
 * Optional reference fields with a "None"/"Aucun" option in the quote form
 * (Ticket, Contractual Manager, Price List). Clearing one sets '' in form state.
 *
 * The update payload (`toUpdateQuoteV4Payload`) is gated on key presence, so a
 * field dropped for being '' never reaches the backend and its old value
 * survives on reopen (#100102 follow-up). These fields are therefore sent as
 * `null` when cleared, so the update actually clears the column.
 */
export const CLEARABLE_QUOTE_FIELDS = new Set(['ticket', 'contractual_manager', 'pricelist']);

export interface CollectQuoteSubmitOptions {
  /** Keys where an empty array means "clear all" and must be sent (e.g. skus). */
  sendEmptyArrays?: Set<string>;
  /** Keys sent as `null` when '' (cleared to None) instead of being dropped. */
  clearable?: Set<string>;
  /** Form-only keys to exclude entirely (transformed elsewhere). */
  skip?: Set<string>;
}

/**
 * Build the field map to submit from the quote form state.
 *
 * Empty strings are dropped so a field the user never set is not sent — with two
 * exceptions: a `clearable` field at '' is sent as `null` (so clearing it to
 * "None" persists), and an empty array in `sendEmptyArrays` is sent (so removing
 * every line item persists). Everything else keeps its value.
 */
export function collectQuoteSubmitFields(
  formData: Record<string, unknown>,
  opts: CollectQuoteSubmitOptions = {},
): Record<string, unknown> {
  const sendEmptyArrays = opts.sendEmptyArrays ?? new Set<string>();
  const clearable = opts.clearable ?? CLEARABLE_QUOTE_FIELDS;
  const skip = opts.skip ?? new Set<string>();
  const data: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(formData)) {
    if (skip.has(key)) continue;
    if (value === '' && clearable.has(key)) {
      data[key] = null;
      continue;
    }
    if (value !== '' && value !== undefined && value !== null) {
      if (Array.isArray(value) && value.length === 0 && !sendEmptyArrays.has(key)) continue;
      data[key] = value;
    }
  }

  return data;
}
